/**
 * Avisos — la campana de la barra superior (petición del propietario,
 * 2026-09-21: "alarmas o avisos a tener en cuenta por el usuario").
 *
 * Sólo avisa de lo que la API YA sabe: datos fiscales, verificación en dos
 * pasos, límites del plan (`URS §54`: avisos al 80/90/100 %, `§110`: nunca un
 * "LIMIT_REACHED" sin explicar) y presupuestos enviados sin respuesta. Nada
 * inventado ni calculado aquí: los porcentajes los da la API (`warningLevel`);
 * de las plazas de cliente sólo se mira si quedan libres.
 *
 * Desde el 2026-09-24 están también los rechazos de la AEAT y las incidencias
 * de envío (Orden art. 16.4), que son los avisos que de verdad no pueden
 * perderse: van los PRIMEROS.
 */
import { useEffect, useRef, useState } from "react";
import type { CuotasPresupuestos, EstadoMfa, EstadoRemision, Plazas, UsoCuota } from "../api/cliente";
import { t } from "../i18n/es";
import { importeLegible } from "../ui/formato";
import { Icono } from "../ui/Iconos";
import { pasosDelAlta, pasosPorHacer, type DatosDelAlta, type PasoAlta } from "../ui/pasos-del-alta";

export type DestinoAviso =
  | "emisor"
  | "seguridad"
  | "presupuestos"
  | "clientes"
  | "productos"
  | "facturas"
  | "autorizacion"
  | "compras"
  | "tareas"
  // OD-0062 E-11: Cobros pendientes con el filtro «Vencidas».
  | "cobros";

/**
 * De qué habla un aviso, que decide DÓNDE se enseña.
 *
 * ```text
 * alta       lo que falta para poder emitir: datos fiscales, autorizacion de
 *            la AEAT, segundo factor. Se termina UNA vez y no vuelve
 * operacion  lo que pasa mientras trabajas: rechazos de la AEAT, registros sin
 *            remitir, cuotas al limite, presupuestos sin respuesta
 * ```
 *
 * Hasta el 2026-10-15 no había distinción, y lo pendiente del alta se contaba
 * TRES veces a la vez —el diálogo de la puerta, la tarjeta de Inicio y la
 * campana—: con una cuenta recién creada los tres decían lo mismo. Un aviso
 * repetido no avisa el triple; enseña tres sistemas y ninguno manda.
 */
export type ClaseAviso = "alta" | "operacion";

export type Aviso = {
  readonly id: string;
  readonly tono: "aviso" | "info";
  readonly texto: string;
  readonly destino: DestinoAviso;
  readonly clase: ClaseAviso;
};

export type DatosAvisos = {
  /**
   * Lo del alta, de LA lista de pasos (`ui/pasos-del-alta.ts`): la misma que
   * pinta el asistente y Configuración › Primeros pasos.
   *
   * La autorización entra aquí desde `OD-0039` E-11: el diálogo del alta se
   * puede cerrar, así que lo que falta tiene que seguir viéndose en algún
   * sitio. Si no, cerrar equivaldría a olvidarlo, y el día de la primera
   * factura la sorpresa sería la misma que el muro quería evitar.
   */
  readonly alta: DatosDelAlta;
  /** Sólo para los códigos de recuperación: el segundo factor del alta va en `alta`. */
  readonly mfa: EstadoMfa | null;
  readonly cuotas: CuotasPresupuestos | null;
  readonly plazas: Plazas | null;
  readonly enviados: number | null;
  /** Orden art. 16.4: registros sin enviar y rechazos de la AEAT. */
  readonly remision: EstadoRemision | null;
  /**
   * `OD-0053 D-07`, pieza 5: los borradores de ANTICIPO sin emitir. Se lee de
   * los borradores, sin job: mientras haya uno, se avisa. `null` = no se sabe.
   */
  readonly anticiposSinEmitir?: { readonly count: number; readonly oldestCreatedAt: string | null } | null;
  /**
   * `OD-0054 D-05`: los pagos a proveedores sin su factura, y cuántos ya van
   * tarde (lo cuenta la API). `null` si no se sabe o el rol no ve compras.
   */
  readonly pagosSinFactura?: { readonly sinFactura: number; readonly tarde: number } | null;
  /**
   * `OD-0048-ENMIENDA-02` F-02: las tareas del CRM con la fecha ya pasada, en el
   * día del usuario. Se cuentan al cargar el panel, como el resto: sin correo,
   * sin cron y sin push (`OD-0050` D-05). `null` fuera de Business o si no se sabe.
   */
  readonly tareasVencidas?: number | null;
  /** `OD-0062` E-11: las tareas de HOY, con la misma regla. `null` fuera de Business. */
  readonly tareasHoy?: number | null;
  /**
   * `OD-0062` E-11: las facturas VENCIDAS y lo que queda por cobrar de ellas, de
   * la misma función que Cobros pendientes (`OD-0051` D-03: solo las que tienen
   * vencimiento y ya pasó). Lo suma la API. `null` fuera de Business o si no se sabe.
   */
  readonly facturasVencidas?: { readonly facturas: number; readonly pendiente: string } | null;
};

/** Un instante de la API -> "15/10", en hora de Madrid, que es la del usuario. */
function diaYMes(instante: string | null): string | null {
  if (instante === null) return null;
  const d = new Date(instante);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("es-ES", { timeZone: "Europe/Madrid", day: "2-digit", month: "2-digit" }).format(d);
}

function avisoCuota(id: string, nombre: string, uso: UsoCuota, destino: DestinoAviso, reinicio: string | null): Aviso | null {
  // Sin tope no hay porcentaje, y sin porcentaje no hay aviso que dar. El
  // `limit === null` es redundante con `warningLevel` —la API no manda nivel
  // sin tope— y está por escrito igualmente: de él depende el texto de abajo.
  if (uso.warningLevel === null || uso.limit === null) return null;
  const lleno = uso.available === 0;
  return {
    id,
    // Una cuota al limite pasa MIENTRAS trabajas, no es un paso del alta.
    clase: "operacion",
    tono: lleno ? "aviso" : "info",
    texto: lleno ? t.avisos.cuotaLlena(nombre, uso.limit, reinicio) : t.avisos.cuotaCerca(nombre, uso.used, uso.limit, reinicio),
    destino,
  };
}

/**
 * Un paso del alta por hacer, como tarea de la tarjeta. El texto depende de
 * en qué punto está: no es lo mismo «faltan los datos» que «confírmalos», ni
 * «autorízanos» que «lo estamos comprobando».
 */
function avisoDelPaso(paso: PasoAlta, d: DatosDelAlta): Aviso | null {
  const espera = paso.estado === "espera";
  switch (paso.id) {
    case "fiscal":
      return {
        id: "emisor",
        clase: "alta",
        tono: "aviso",
        texto: !espera ? t.avisos.emisorSinDatos : d.sii === null ? t.avisos.emisorSinSii : t.avisos.emisorSinConfirmar,
        destino: "emisor",
      };
    case "autorizacion":
      return espera
        ? { id: "autorizacion", clase: "alta", tono: "info", texto: t.avisos.autorizacionComprobando, destino: "autorizacion" }
        : { id: "autorizacion", clase: "alta", tono: "aviso", texto: t.avisos.autorizacionPendiente, destino: "autorizacion" };
    case "mfa":
      return { id: "mfa", clase: "alta", tono: "info", texto: t.avisos.mfaSinActivar, destino: "seguridad" };
    case "cliente":
      return { id: "paso-cliente", clase: "alta", tono: "info", texto: t.inicio.pasos.cliente, destino: "clientes" };
    case "factura":
      return { id: "paso-factura", clase: "alta", tono: "info", texto: t.inicio.pasos.factura, destino: "facturas" };
    default:
      return null;
  }
}

/** Lo más urgente primero: lo que impide trabajar, luego lo que conviene. */
export function calcularAvisos(d: DatosAvisos): readonly Aviso[] {
  const lista: Array<Aviso | null> = [];
  // Lo primero de todo: lo que la Orden (art. 16.4) obliga a decir mientras
  // queden registros sin enviar, y lo que la AEAT ha rechazado.
  if (d.remision !== null && d.remision.rejected > 0) {
    lista.push({ id: "remision-rechazos", clase: "operacion", tono: "aviso", texto: t.avisos.remisionRechazos(d.remision.rejected), destino: "facturas" });
  }
  if (d.remision !== null && d.remision.pending > 0) {
    lista.push({
      id: "remision-pendientes",
      clase: "operacion",
      tono: d.remision.incident ? "aviso" : "info",
      texto: d.remision.incident
        ? t.avisos.remisionIncidencia(d.remision.pending)
        : t.avisos.remisionPendientes(d.remision.pending),
      destino: "facturas",
    });
  }
  // El IVA de un anticipo se devenga al cobrar: un borrador es una factura que va con retraso.
  if (d.anticiposSinEmitir != null && d.anticiposSinEmitir.count > 0) {
    lista.push({
      id: "anticipos-sin-emitir",
      clase: "operacion",
      tono: "aviso",
      texto: t.avisos.anticiposSinEmitir(d.anticiposSinEmitir.count, diaYMes(d.anticiposSinEmitir.oldestCreatedAt)),
      destino: "facturas",
    });
  }
  // Sin la factura del anticipo no se deduce su IVA: pasa mientras trabajas.
  if (d.pagosSinFactura != null && d.pagosSinFactura.sinFactura > 0) {
    lista.push({
      id: "compras-sin-factura",
      clase: "operacion",
      tono: d.pagosSinFactura.tarde > 0 ? "aviso" : "info",
      texto: t.avisos.pagosSinFactura(d.pagosSinFactura.sinFactura, d.pagosSinFactura.tarde),
      destino: "compras",
    });
  }
  // Una factura vencida es dinero que ya debías tener: pasa mientras trabajas.
  if (d.facturasVencidas != null && d.facturasVencidas.facturas > 0) {
    lista.push({
      id: "facturas-vencidas",
      clase: "operacion",
      tono: "aviso",
      texto: t.avisos.facturasVencidas(d.facturasVencidas.facturas, importeLegible(d.facturasVencidas.pendiente)),
      destino: "cobros",
    });
  }
  // Una tarea vencida es algo que ya debías haber hecho: pasa mientras trabajas.
  if (d.tareasVencidas != null && d.tareasVencidas > 0) {
    lista.push({ id: "tareas-vencidas", clase: "operacion", tono: "aviso", texto: t.avisos.tareasVencidas(d.tareasVencidas), destino: "tareas" });
  }
  // Las de hoy no van tarde todavía: se dicen, sin el tono de lo urgente.
  if (d.tareasHoy != null && d.tareasHoy > 0) {
    lista.push({ id: "tareas-hoy", clase: "operacion", tono: "info", texto: t.avisos.tareasHoy(d.tareasHoy), destino: "tareas" });
  }
  // Lo del alta: los pasos por hacer de LA lista, cada uno una vez.
  for (const paso of pasosPorHacer(pasosDelAlta(d.alta))) lista.push(avisoDelPaso(paso, d.alta));
  // Sin tope no hay plazas que llenar: `available` viene a null, no a cero.
  if (d.plazas !== null && d.plazas.available === 0 && d.plazas.total !== null) {
    lista.push({ id: "plazas", clase: "operacion", tono: "aviso", texto: t.avisos.plazasLlenas(d.plazas.total), destino: "clientes" });
  }
  if (d.cuotas !== null) {
    const mes = d.cuotas.quotesCreatedThisMonth;
    lista.push(avisoCuota("cuota-mes", t.avisos.nombres.presupuestosMes, mes, "presupuestos", mes.resetsOn));
    lista.push(avisoCuota("cuota-abiertos", t.avisos.nombres.presupuestosAbiertos, d.cuotas.quotesActive, "presupuestos", null));
    lista.push(avisoCuota("cuota-productos", t.avisos.nombres.productos, d.cuotas.products, "productos", null));
  }
  if (d.mfa !== null && d.mfa.totp === "ACTIVE" && d.mfa.codigosRestantes <= 2) {
    lista.push({ id: "codigos", clase: "operacion", tono: "aviso", texto: t.avisos.pocosCodigos(d.mfa.codigosRestantes), destino: "seguridad" });
  }
  if (d.enviados !== null && d.enviados > 0) {
    lista.push({ id: "enviados", clase: "operacion", tono: "info", texto: t.avisos.enviados(d.enviados), destino: "presupuestos" });
  }
  const avisos = lista.filter((a): a is Aviso => a !== null);
  return [...avisos.filter((a) => a.tono === "aviso"), ...avisos.filter((a) => a.tono === "info")];
}

/**
 * Lo que enseña LA CAMPANA: sólo lo de `operacion`.
 *
 * Lo del alta NO desaparece —`OD-0039` E-11 exige que lo que falta siga a la
 * vista después de cerrar el diálogo—: vive en la tarjeta de Inicio, que es
 * permanente y no se puede cerrar. Lo que se quita es la TERCERA copia.
 *
 * La campana es una bandeja de notificaciones, y lo del alta no es una
 * notificación: no llega, no cambia solo y se termina una vez. Mezclarlos hacía
 * que la insignia dijera «4» el primer día de una cuenta donde no había pasado
 * absolutamente nada.
 */
export function avisosDeLaCampana(avisos: readonly Aviso[] | null): readonly Aviso[] | null {
  return avisos === null ? null : avisos.filter((a) => a.clase === "operacion");
}

/**
 * Lo que enseña LA TARJETA de Inicio: todo, y lo del alta primero.
 *
 * Es la lista única de pendientes. Antes esta tarjeta mezclaba dos modelos de
 * tarea: una fila que contaba «quedan N pasos» y, debajo, los avisos. `fiscal`
 * salía en las DOS —contado y avisado— mientras que «tu primer cliente» y
 * «emitir tu primera factura» salían sólo en la cuenta. Por eso el número no
 * cuadraba nunca con las filas de debajo.
 */
export function avisosDeLaTarjeta(avisos: readonly Aviso[] | null): readonly Aviso[] | null {
  if (avisos === null) return null;
  return [...avisos.filter((a) => a.clase === "alta"), ...avisos.filter((a) => a.clase === "operacion")];
}

export function CampanaAvisos({ avisos: todos, onIr }: { avisos: readonly Aviso[] | null; onIr: (destino: DestinoAviso) => void }) {
  // La insignia cuenta LO QUE SE VE. Contar lo que no se enseña es la forma
  // mas rapida de que nadie vuelva a abrir la campana.
  const avisos = avisosDeLaCampana(todos);
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLDivElement>(null);

  // Se cierra al pulsar fuera o con Escape, como cualquier menú.
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent): void => {
      if (caja.current !== null && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    const tecla = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setAbierto(false);
    };
    document.addEventListener("mousedown", fuera);
    document.addEventListener("keydown", tecla);
    return () => {
      document.removeEventListener("mousedown", fuera);
      document.removeEventListener("keydown", tecla);
    };
  }, [abierto]);

  const cuantos = avisos?.length ?? 0;
  const urgentes = avisos?.filter((a) => a.tono === "aviso").length ?? 0;

  return (
    <div className="avisos" ref={caja}>
      <button
        type="button"
        className="avisos-boton"
        aria-expanded={abierto}
        aria-controls="avisos-lista"
        aria-label={t.avisos.boton(cuantos)}
        onClick={() => setAbierto((v) => !v)}
      >
        <Icono nombre="campana" />
        {cuantos > 0 ? (
          <span className={urgentes > 0 ? "avisos-cuenta avisos-cuenta-urgente" : "avisos-cuenta"} aria-hidden="true">
            {cuantos}
          </span>
        ) : null}
      </button>
      {abierto ? (
        <div className="avisos-panel" id="avisos-lista">
          <p className="avisos-titulo">{t.avisos.titulo}</p>
          {avisos === null ? <p className="ayuda-neutra">{t.avisos.cargando}</p> : null}
          {avisos !== null && avisos.length === 0 ? <p className="ayuda-neutra">{t.avisos.ninguno}</p> : null}
          {avisos !== null && avisos.length > 0 ? (
            <ul className="avisos-lista">
              {avisos.map((a) => (
                <li key={a.id} className={`avisos-item avisos-item-${a.tono}`}>
                  <span>{a.texto}</span>
                  <button
                    type="button"
                    className="avisos-ir"
                    onClick={() => {
                      setAbierto(false);
                      onIr(a.destino);
                    }}
                  >
                    {t.avisos.ir[a.destino]}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
